import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styles from "./residentsTab.module.scss";
import iconGrid from "@source/assets/icons/iconGrid.svg";
import iconLines from "@source/assets/icons/iconLines.svg";
import iconSelectResidents from "@source/assets/icons/iconSelectResidents.svg";
import iconClearDate from "@source/assets/icons/iconClearDate.svg";
import Svg from "@source/components/UI/svg/svg";
import SearchInput from "@source/components/UI/inputs/searchInput/searchInput";
import ResidentCard from "@source/components/residentCard/residentCard";
import { IFillVariant } from "@source/utils/enums";
import useFetching from "@source/hooks/useFetching";
import ResidentController from "@source/api/controllers/residentController";
import residentStore from "@source/store/residentsStore";
import Loader from "@source/components/UI/loader/loader";
import nursingHomeStore from "@source/store/nursingHomeStore";
import { observer } from "mobx-react-lite";
import BlockNewUpdatesModal from "@components/blockNewUpdatesModal/blockNewUpdatesModal";
import residentsStore from "@source/store/residentsStore";
import NewUpdatesDisabledModal from "@components/newUpdatesDisabledModal/newUpdatesDisabledModal";
import { IResident, ResidentStatus } from "@source/api/models/residentModel";
import Button from "@components/UI/buttons/button/button";
import { HOME_ROUTES } from "@source/utils/utils";
import userStore from "@store/userStore";
import { HomePermissionsEnum } from "@source/api/models/permissionsModels";
import { ISelectItem } from "@components/UI/inputs/autocompleteInput/autocompleteInput";
import MultiSelectInput from "@components/UI/inputs/multiSelectInput/multiSelectInput";
import { ContactTypeEnum } from "@source/api/models/allUsersModels";
import appStore from "@store/appStore";
import SectionGroupController from "@source/api/controllers/sectionGroupController";
import { ISectionGroup } from "@source/api/models/sectionGroupModels";

const ResidentsTab = observer(() => {
  const [resident, isLoading] = useFetching<ReturnType<typeof ResidentController.getResidents>>(
    ResidentController.getResidents,
  );
  const [, getResidentsCountIsLoading] = useFetching<ReturnType<typeof ResidentController.getResidentsCount>>(
    ResidentController.getResidentsCount,
  );
  const [getSections] = useFetching<ReturnType<typeof ResidentController.getSections>>(ResidentController.getSections);

  const router = useHistory();

  const [searchString, setSearchString] = useState("");
  const [selectedSectionValues, setSelectedSectionValues] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [searchStringDirty, setSearchStringDitry] = useState(false);
  const [gridVariant, setGridVariant] = useState(GridVariant.Grid);
  const [sectionItems, setSectionItems] = useState<ISelectItem[]>([]);
  const [sectionGroupList, setSectionGroupList] = useState<ISectionGroup[]>([]);

  const sectionGroupsEnabled = Boolean(
    nursingHomeStore.getActiveNursingHome()?.organization?.sectionGroupsEnabled,
  );

  const groupSelectItems: ISelectItem[] = sectionGroupList.map((g) => ({
    title: g.name,
    value: g._id,
    id: g._id,
  }));

  useEffect(() => {
    return () => {
      if (residentStore.getFocusOnResident()) {
        residentStore.setFocusOnResident(null);
      }
    };
  }, []);

  const buildResidentsRequest = () => ({
    nursingHomeId: nursingHomeStore.getId(),
    searchString,
    section: "all" as const,
    ...(selectedSectionValues.length > 0 ? { sections: selectedSectionValues.join(",") } : {}),
  });

  useEffect(() => {
    resetFilters();
    setSelectedSectionValues([]);
    setSelectedGroupIds([]);
    setSectionGroupList([]);

    const fetchSectionsNames = async () => {
      const sectionsRes = await getSections(nursingHomeStore.activeNursingHome?._id);

      if (sectionsRes) {
        setSectionItems(
          sectionsRes.map((section) => ({
            title: section._id,
            value: section._id,
            id: section._id,
          })),
        );
      }
    };

    const fetchGroups = async () => {
      if (!sectionGroupsEnabled || !nursingHomeStore.activeNursingHome?._id) {
        return;
      }
      try {
        const groups = await SectionGroupController.getByNursingHome(nursingHomeStore.activeNursingHome._id);
        const list = groups || [];
        setSectionGroupList(list);
      } catch {
        setSectionGroupList([]);
      }
    };

    async function loadResidents() {
      const residentsRes = await resident({
        nursingHomeId: nursingHomeStore.getId(),
        searchString: "",
        section: "all",
      });

      if (residentsRes) {
        const residentsCountRes = await ResidentController.getResidentsCount(nursingHomeStore.getId());

        residentStore.setResidents(residentsRes);
        if (residentsCountRes) {
          residentStore.setResidentsCount(residentsCountRes);
        }
      }
    }

    loadResidents();
    fetchSectionsNames();
    fetchGroups();
  }, [nursingHomeStore.activeNursingHome?._id, sectionGroupsEnabled]);

  useEffect(() => {
    if (searchStringDirty) {
      const timer = setTimeout(() => {
        async function getResidentsDebounced() {
          const residentsRes = await resident(buildResidentsRequest());

          if (residentsRes) {
            residentStore.setResidents(residentsRes);
          }
        }
        getResidentsDebounced();
      }, 500);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [searchString, selectedSectionValues]);

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);

    if (!searchStringDirty) {
      setSearchStringDitry(true);
    }
  };

  const onGridVariant = (variant: GridVariant) => {
    setGridVariant(variant);
  };

  const isActiveGridVariant = (variant: GridVariant) => {
    return gridVariant === variant;
  };

  const resetFilters = () => {
    setSearchString("");
    setSearchStringDitry(false);
  };

  const isSelectableResident = () => {
    return !!residentStore.getMultipleUpdateSelectedResidents();
  };

  const onSelectResidents = () => {
    residentStore.setMultipleUpdateSelectedResidents([]);
  };

  const onCancelSelectResidents = () => {
    residentStore.setMultipleUpdateSelectedResidents(null);
  };

  const isPrimaryContactConfirmed = (resident) => {
    return resident.familyRelations.some(
      (familyRelation) =>
        familyRelation.contactType === ContactTypeEnum.primary &&
        !familyRelation?.familyMember?.account?.isTemporaryPassword &&
        !familyRelation?.familyMember?.account?.nonParticipant &&
        familyRelation?.familyMember?.isConfirmed &&
        !familyRelation?.familyMember?.isDisabled &&
        resident.updatesEnabled,
    );
  };

  const onSelectAll = () => {
    const allResidents = residentsStore.getResidents().filter((resident) => {
      const canSelect = isPrimaryContactConfirmed(resident) && resident.status === ResidentStatus.ACTIVE;
      return canSelect;
    });

    residentStore.setMultipleUpdateSelectedResidents(allResidents);
  };

  const onNewUpdateResidents = () => {
    const multipleUpdateSelectedResidents = residentStore.getMultipleUpdateSelectedResidents();

    if (multipleUpdateSelectedResidents) {
      router.push({
        pathname: `${HOME_ROUTES.Residents}/${multipleUpdateSelectedResidents[0]._id}/update`,
      });
    }
  };

  const onSectionsChange = (next: string[]) => {
    setSelectedSectionValues(next);
    if (!searchStringDirty) {
      setSearchStringDitry(true);
    }
    residentsStore.setMultipleUpdateSelectedResidents(null);
  };

  const onGroupIdsChange = (nextGroupIds: string[]) => {
    const prev = selectedGroupIds;
    const added = nextGroupIds.filter((id) => !prev.includes(id));
    const removed = prev.filter((id) => !nextGroupIds.includes(id));
    setSelectedGroupIds(nextGroupIds);

    setSelectedSectionValues((prevSections) => {
      const next = new Set(prevSections);
      for (const id of added) {
        sectionGroupList
          .find((g) => g._id === id)
          ?.sections.forEach((s) => next.add(s));
      }
      for (const id of removed) {
        const removedGroup = sectionGroupList.find((g) => g._id === id);
        for (const s of removedGroup?.sections ?? []) {
          const stillInOtherGroup = nextGroupIds.some((gid) =>
            sectionGroupList.find((g) => g._id === gid)?.sections.includes(s),
          );
          if (!stillInOtherGroup) {
            next.delete(s);
          }
        }
      }
      return Array.from(next);
    });

    if (!searchStringDirty) {
      setSearchStringDitry(true);
    }
    residentsStore.setMultipleUpdateSelectedResidents(null);
  };

  return (
    <div className={styles.residents}>
      {residentsStore.getModalBlockNewUpdatesResident() ? <BlockNewUpdatesModal /> : <></>}

      {residentsStore.getModalNewUpdatesDisabledResident() ? <NewUpdatesDisabledModal /> : <></>}

      <div className={styles.filters}>
        <div className={styles.fields}>
          {appStore.getIsMobile() ? (
            <div className={styles.searchAndSelect}>
              <SearchInput
                className={styles.search}
                value={searchString}
                onChange={onSearchChange}
                fill={IFillVariant.Fill}
                placeHolder="Search"
              />
              {sectionGroupsEnabled && groupSelectItems.length > 0 ? (
                <MultiSelectInput
                  className={styles.search}
                  id="sectionGroupSelect"
                  placeholder="Homes"
                  label=""
                  items={groupSelectItems}
                  value={selectedGroupIds}
                  onChange={onGroupIdsChange}
                  showSelectAll={groupSelectItems.length > 0}
                  selectAllLabel="Select all homes"
                />
              ) : null}
              <MultiSelectInput
                className={styles.search}
                id="sectionSelect"
                placeholder="All sections"
                items={sectionItems}
                value={selectedSectionValues}
                onChange={onSectionsChange}
                showSelectAll={sectionItems.length > 0}
                selectAllLabel="Select all sections"
              />
            </div>
          ) : (
            <>
              <SearchInput
                className={styles.search}
                value={searchString}
                onChange={onSearchChange}
                fill={IFillVariant.Fill}
                placeHolder="Search"
              />
              {sectionGroupsEnabled && groupSelectItems.length > 0 ? (
                <MultiSelectInput
                  className={styles.search}
                  id="sectionGroupSelect"
                  placeholder="Homes"
                  items={groupSelectItems}
                  value={selectedGroupIds}
                  onChange={onGroupIdsChange}
                  showSelectAll={groupSelectItems.length > 0}
                  selectAllLabel="Select all homes"
                />
              ) : null}
              <MultiSelectInput
                className={styles.search}
                id="sectionSelect"
                placeholder="All sections"
                items={sectionItems}
                value={selectedSectionValues}
                onChange={onSectionsChange}
                showSelectAll={sectionItems.length > 0}
                selectAllLabel="Select all sections"
              />
            </>
          )}

          {isSelectableResident() ? (
            <>
              <Button className={styles.selectAll} onClick={onSelectAll}>
                Select All
              </Button>

              <Button className={styles.cancelSelection} onClick={onCancelSelectResidents}>
                <div className={styles.container}>
                  <img className={styles.icon} src={iconClearDate} alt="" />

                  <span className={styles.text}>Cancel selection</span>
                </div>
              </Button>

              <Button
                className={styles.newUpdate}
                onClick={onNewUpdateResidents}
                disabled={!residentStore.getMultipleUpdateSelectedResidents()?.length}
              >
                {`New Update (${residentStore.getMultipleUpdateSelectedResidents()?.length})`}
              </Button>
            </>
          ) : userStore.isPermission(HomePermissionsEnum.ADD_MULTIPLE_UPDATE) ? (
            <Button className={styles.selectResidents} onClick={onSelectResidents}>
              <div className={styles.container}>
                <img className={styles.icon} src={iconSelectResidents} alt="" />

                <span className={styles.text}>Multiple residents update</span>
              </div>
            </Button>
          ) : (
            <></>
          )}
        </div>

        {isSelectableResident() || appStore.getIsMobile() ? (
          <></>
        ) : (
          <div className={styles.gridFormat}>
            <Svg
              className={`${styles.icon} ${isActiveGridVariant(GridVariant.Grid) && styles.icon_active}`}
              src={iconGrid}
              width={40}
              height={40}
              onClick={() => {
                onGridVariant(GridVariant.Grid);
              }}
            />

            <Svg
              className={`${styles.icon} ${isActiveGridVariant(GridVariant.Lines) && styles.icon_active}`}
              src={iconLines}
              width={40}
              height={40}
              onClick={() => {
                onGridVariant(GridVariant.Lines);
              }}
            />
          </div>
        )}
      </div>

      {isLoading || getResidentsCountIsLoading ? (
        <div className={styles.loading}>
          <Loader className={styles.loading__top_loader} height={80} width={80} borderWidth={6} />
        </div>
      ) : (
        <div className={`${styles.grid} ${gridVariant === GridVariant.Lines && styles.grid_lines}`}>
          {residentStore.getResidents().map((resident: IResident) => (
            <ResidentCard key={resident._id} resident={resident} gridVariant={gridVariant} />
          ))}
        </div>
      )}
    </div>
  );
});

export default ResidentsTab;

export const enum GridVariant {
  Grid,
  Lines,
}
